import { hasPermission } from '@hq/permission';
import { Button, Result } from 'antd';
import { Link, Navigate, createBrowserRouter, useLocation } from 'react-router-dom';
import { Suspense, lazy, type ReactElement } from 'react';
import AppLayout from '../layouts/AppLayout';
import AiCenter from '../pages/AiCenter';
import ApprovalFlows from '../pages/ApprovalFlows';
import AuditLogs from '../pages/AuditLogs';
import BudgetOps from '../pages/BudgetOps';
import ContractOps from '../pages/ContractOps';
import Dashboard from '../pages/Dashboard';
import DeliveryOps from '../pages/DeliveryOps';
import DeliveryResourceOps from '../pages/DeliveryResourceOps';
import InboundOps from '../pages/InboundOps';
import InquiryOps from '../pages/InquiryOps';
import FreeAssetOps from '../pages/FreeAssetOps';
import InterfaceLogs from '../pages/InterfaceLogs';
import Login from '../pages/Login';
import MaterialOps from '../pages/MaterialOps';
import OrderCenter from '../pages/OrderCenter';
import OpsPerfPage from '../pages/OpsPerfPage';
import PriceChange from '../pages/PriceChange';
import PurchaseOps from '../pages/PurchaseOps';
import RfDevices from '../pages/RfDevices';
import ScreenDevices from '../pages/ScreenDevices';
import ScreenScan from '../pages/ScreenScan';
import ServiceCenter from '../pages/ServiceCenter';
import ServiceFeeOps from '../pages/ServiceFeeOps';
import ServiceSatisfaction from '../pages/ServiceSatisfaction';
import ServiceWorkorder from '../pages/ServiceWorkorder';
import SettleOps from '../pages/SettleOps';
import OrgConfig from '../pages/OrgConfig';
import OrgGroups from '../pages/OrgGroups';
import OrgManage from '../pages/OrgManage';
import OrgSpace from '../pages/OrgSpace';
import OrgStructure from '../pages/OrgStructure';
import ShortageOps from '../pages/ShortageOps';
import StockAlertOps from '../pages/StockAlertOps';
import StockRuleOps from '../pages/StockRuleOps';
import Substitute from '../pages/Substitute';
import SupplierOps from '../pages/SupplierOps';
import SystemDict from '../pages/SystemDict';
import SystemUsers from '../pages/SystemUsers';
import WaveOps from '../pages/WaveOps';
import WmsOps from '../pages/WmsOps';
import WmsStock from '../pages/WmsStock';
import WmsSupply from '../pages/WmsSupply';
import { useAuthStore } from '../session';

// 运营大屏（/screen）：图表重、单独懒加载，登录端首屏不携带大屏代码
const ScreenPage = lazy(() => import('../pages/ScreenPage'));

/** 路由守卫：无令牌跳登录；最终权限仍以后端为准（docs/05 §5）。 */
function RequireAuth({ children }: { children: ReactElement }) {
  const token = useAuthStore((state) => state.token);
  const location = useLocation();
  if (!token) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
}

/**
 * 路由级权限门禁（纵深防御，口径与侧栏菜单完全一致）：
 * 菜单裁剪只是"看不见"，直接敲 URL 仍能进页面 —— 用户会看到一个处处 403 的残页。
 * 后端始终校验，这里只把失败提前成一句明确提示。
 */
const ROUTE_PERMS: Record<string, string[]> = {
  'order': ['wl:purchase:manage'],
  // 外部仓 WMS：与侧栏一级分组同口径（库存或采购任一权限码）
  'wms': ['wl:inventory:manage', 'wl:purchase:manage'],
  'wms/ops': ['wl:inventory:manage', 'wl:purchase:manage'],
  'wms/stock': ['wl:inventory:manage', 'wl:purchase:manage'],
  'purchase': ['wl:purchase:manage'],
  'purchase/supplier': ['wl:purchase:manage'],
  'purchase/contract': ['wl:purchase:manage'],
  'purchase/inquiry': ['wl:purchase:manage'],
  'purchase/price-change': ['wl:purchase:manage'],
  'purchase/free-assets': ['wl:purchase:manage'],
  'purchase/stock-rule': ['wl:purchase:manage'],
  'shortage': ['wl:purchase:manage'],
  'substitute': ['wl:purchase:manage'],
  'ai': ['wl:purchase:manage'],
  'inventory/alert': ['wl:inventory:manage', 'wl:purchase:manage'],
  'inventory/inbound': ['wl:inventory:manage', 'wl:purchase:manage'],
  'system/users': ['sys:user:manage'],
  'system/approval': ['sys:approval:manage'],
  'system/org-config': ['sys:config:manage'],
  'system/dict': ['sys:config:manage'],
  'system/audit': ['sys:audit:view'],
  'system/interface-log': ['sys:audit:view'],
  'system/screen-devices': ['sys:config:manage'],
  'system/screen-scan': ['sys:config:manage'],
  'system/rf-devices': ['sys:config:manage'],
  'system/orgs': ['sys:org:manage'],
  'system/org-groups': ['sys:org:manage'],
  'system/org-structure': ['sys:org:manage'],
  'system/org-space': ['sys:org:manage'],
  'service': ['sv:service:handle'],
  'service/workorder': ['sv:service:handle'],
  'service/satisfaction': ['sv:service:handle'],
  // 物资主数据：查询对所有登录账号开放，新建/维护需 wl:purchase:manage（后端 MaterialEndpoints 同口径）
  'purchase/material': ['wl:purchase:manage'],
  'settle': ['wl:settle:manage'],
  'settle/service-fee': ['wl:settle:manage'],
  'settle/budget': ['wl:settle:manage'],
  'delivery/wave': ['wl:delivery:manage'],
  'delivery/resource': ['wl:delivery:manage'],
  'delivery/perf': ['wl:analytics:view']
};

function RequirePerm({ path, children }: { path: string; children: ReactElement }) {
  const profile = useAuthStore((state) => state.profile);
  const required = ROUTE_PERMS[path];

  if (!required?.length || profile?.level === 'admin') return children;

  const allowed = profile?.perms ? required.some((perm) => hasPermission(profile.perms, perm)) : false;
  if (allowed) return children;

  return (
    <Result
      status="403"
      title="无权访问"
      subTitle={`当前账号缺少 ${required.join(' 或 ')} 权限，请联系系统管理员。`}
      extra={
        <Link to="/dashboard">
          <Button type="primary">返回运营看板</Button>
        </Link>
      }
    />
  );
}

/** 未匹配路由：明确展示 404，而不是静默跳回看板（用户会以为链接点错了）。 */
function NotFound() {
  return (
    <Result
      status="404"
      title="404"
      subTitle="页面不存在或已被移除，请从左侧菜单重新进入。"
      extra={
        <Link to="/dashboard">
          <Button type="primary">返回运营看板</Button>
        </Link>
      }
    />
  );
}

export const router = createBrowserRouter(
  [
  { path: '/login', element: <Login /> },
  {
    // 大屏全屏路由：不套 AppLayout、不走账号登录态门禁（屏端凭「设备凭据」独立换只读令牌），
    // 未授权时页内展示激活页（屏端凭设备凭据独立取数，与账号登录态无关）
    path: '/screen',
    element: (
      <Suspense fallback={null}>
        <ScreenPage />
      </Suspense>
    )
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: <Dashboard /> },
      { path: 'order', element: <RequirePerm path="order"><OrderCenter /></RequirePerm> },
      { path: 'wms', element: <RequirePerm path="wms"><WmsSupply /></RequirePerm> },
      { path: 'wms/stock', element: <RequirePerm path="wms/stock"><WmsStock /></RequirePerm> },
      { path: 'wms/ops', element: <RequirePerm path="wms/ops"><WmsOps /></RequirePerm> },
      { path: 'purchase', element: <RequirePerm path="purchase"><PurchaseOps /></RequirePerm> },
      { path: 'purchase/supplier', element: <RequirePerm path="purchase/supplier"><SupplierOps /></RequirePerm> },
      { path: 'purchase/contract', element: <RequirePerm path="purchase/contract"><ContractOps /></RequirePerm> },
      { path: 'purchase/inquiry', element: <RequirePerm path="purchase/inquiry"><InquiryOps /></RequirePerm> },
      { path: 'purchase/price-change', element: <RequirePerm path="purchase/price-change"><PriceChange /></RequirePerm> },
      { path: 'purchase/material', element: <RequirePerm path="purchase/material"><MaterialOps /></RequirePerm> },
      { path: 'ai', element: <RequirePerm path="ai"><AiCenter /></RequirePerm> },
      { path: 'shortage', element: <RequirePerm path="shortage"><ShortageOps /></RequirePerm> },
      { path: 'substitute', element: <RequirePerm path="substitute"><Substitute /></RequirePerm> },
      { path: 'purchase/stock-rule', element: <RequirePerm path="purchase/stock-rule"><StockRuleOps /></RequirePerm> },
      { path: 'purchase/free-assets', element: <RequirePerm path="purchase/free-assets"><FreeAssetOps /></RequirePerm> },
      { path: 'inventory/alert', element: <RequirePerm path="inventory/alert"><StockAlertOps /></RequirePerm> },
      { path: 'inventory/inbound', element: <RequirePerm path="inventory/inbound"><InboundOps /></RequirePerm> },
      { path: 'system/orgs', element: <RequirePerm path="system/orgs"><OrgManage /></RequirePerm> },
      { path: 'system/org-groups', element: <RequirePerm path="system/org-groups"><OrgGroups /></RequirePerm> },
      { path: 'system/org-structure', element: <RequirePerm path="system/org-structure"><OrgStructure /></RequirePerm> },
      { path: 'system/org-space', element: <RequirePerm path="system/org-space"><OrgSpace /></RequirePerm> },
      { path: 'delivery', element: <DeliveryOps /> },
      { path: 'delivery/perf', element: <RequirePerm path="delivery/perf"><OpsPerfPage /></RequirePerm> },
      { path: 'delivery/wave', element: <RequirePerm path="delivery/wave"><WaveOps /></RequirePerm> },
      { path: 'delivery/resource', element: <RequirePerm path="delivery/resource"><DeliveryResourceOps /></RequirePerm> },
      { path: 'service', element: <RequirePerm path="service"><ServiceCenter /></RequirePerm> },
      { path: 'service/workorder', element: <RequirePerm path="service/workorder"><ServiceWorkorder /></RequirePerm> },
      { path: 'service/satisfaction', element: <RequirePerm path="service/satisfaction"><ServiceSatisfaction /></RequirePerm> },
      { path: 'settle', element: <RequirePerm path="settle"><SettleOps /></RequirePerm> },
      { path: 'settle/service-fee', element: <RequirePerm path="settle/service-fee"><ServiceFeeOps /></RequirePerm> },
      { path: 'settle/budget', element: <RequirePerm path="settle/budget"><BudgetOps /></RequirePerm> },
      { path: 'system/screen-devices', element: <RequirePerm path="system/screen-devices"><ScreenDevices /></RequirePerm> },
      { path: 'system/screen-scan', element: <RequirePerm path="system/screen-scan"><ScreenScan /></RequirePerm> },
      { path: 'system/rf-devices', element: <RequirePerm path="system/rf-devices"><RfDevices /></RequirePerm> },
      { path: 'system/users', element: <RequirePerm path="system/users"><SystemUsers /></RequirePerm> },
      { path: 'system/approval', element: <RequirePerm path="system/approval"><ApprovalFlows /></RequirePerm> },
      { path: 'system/org-config', element: <RequirePerm path="system/org-config"><OrgConfig /></RequirePerm> },
      { path: 'system/dict', element: <RequirePerm path="system/dict"><SystemDict /></RequirePerm> },
      { path: 'system/audit', element: <RequirePerm path="system/audit"><AuditLogs /></RequirePerm> },
      { path: 'system/interface-log', element: <RequirePerm path="system/interface-log"><InterfaceLogs /></RequirePerm> },
      { path: '*', element: <NotFound /> }
    ]
  }
  ],
  {
    // 提前启用 v7 行为，消除控制台 future-flag 警告（行为与 v7 一致，不是仅静音）
    future: {
      v7_relativeSplatPath: true
    }
  }
);
